import { format, parse } from "date-fns";
import { fr } from "date-fns/locale";

import type { ProcedureHearingType } from "@upclaim/sdk/src/data-contracts";

export interface HearingMetadata {
  label: string;
  description: string;
}

/** Port of Court.vue hearingTypesMapping — libellés/descriptions des types d'audience. */
export const HEARING_TYPES_MAPPING: Record<ProcedureHearingType["name"], HearingMetadata> = {
  FIRST_HEARING: {
    label: "Première audience",
    description:
      "Audience d’enrôlement au tribunal de commerce permettant de constater la comparution des parties et d’organiser le calendrier procédural.",
  },

  PROCEDURAL_HEARING: {
    label: "Audience de procédure",
    description:
      "Audience de mise en état au cours de laquelle le tribunal contrôle l’avancement du dossier et fixe les prochaines étapes (échanges de conclusions, pièces, délais).",
  },

  PROCEDURAL_HEARING_FOLLOWING_POSTPONEMENT_BY_US: {
    label: "Audience de procédure (report à notre initiative)",
    description:
      "Audience reprogrammée à la suite d’un renvoi sollicité par le demandeur, généralement pour finaliser les écritures ou produire des pièces complémentaires.",
  },

  PROCEDURAL_HEARING_FOLLOWING_POSTPONEMENT_BY_OPPOSING_PARTY: {
    label: "Audience de procédure (report par la compagnie)",
    description:
      "Audience reprogrammée après un renvoi demandé par la partie adverse, souvent en raison d’un besoin de délai supplémentaire pour préparer sa défense.",
  },

  PLEA_HEARING: {
    label: "Audience de plaidoirie",
    description:
      "L'audience de plaidoirie correspond à la phase finale des débats, où les arguments sont présentés oralement devant le juge chargé d'instruire l'affaire. Dernière audience avant le délibéré.",
  },

  DELIBERATION_HEARING: {
    label: "Audience de délibéré",
    description:
      "Étape au cours de laquelle l’affaire est mise en délibéré ; la décision est rendue à une date proche fixée par le tribunal.",
  },
};

/**
 * Grouped payload of adminAPI.listProcedureHearings({ groupBy: "juridiction" }).
 * The SDK type only models `availableHearings`/`constraint`: the juridiction-name
 * keys are dynamic (Joi can't express them), each mixing juridiction fields with
 * "YYYY-MM-DD" keys holding the hearings booked that day.
 */
export interface GroupedProcedureHearing {
  procedure: {
    claimId: string;
  };
}

export type HearingsByJuridiction = Record<string, Record<string, unknown> | null>;

const DATE_KEY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/** Port of HearingSelection.extractDatesFromJuridiction — "YYYY-MM-DD" keys, ascending. */
export function extractDatesFromJuridiction(
  hearingsByJuridictions: HearingsByJuridiction | undefined,
  juridictionName: string,
): string[] {
  const entry = hearingsByJuridictions?.[juridictionName];
  if (!entry) {
    return [];
  }
  return Object.keys(entry)
    .filter((key) => DATE_KEY_REGEX.test(key))
    .sort();
}

/** Hearings booked at a juridiction on a given "YYYY-MM-DD" date. */
export function getHearingsForDate(
  hearingsByJuridictions: HearingsByJuridiction | undefined,
  juridictionName: string,
  date: string,
): GroupedProcedureHearing[] {
  const value = hearingsByJuridictions?.[juridictionName]?.[date];
  return Array.isArray(value) ? (value as GroupedProcedureHearing[]) : [];
}

/** Port of HearingSelection.isDateInClaimPayload — the claim already has a hearing that day there. */
export function isDateInClaimPayload(
  hearingsByJuridictions: HearingsByJuridiction | undefined,
  date: string,
  juridictionName: string,
  claimId: string,
): boolean {
  return getHearingsForDate(hearingsByJuridictions, juridictionName, date).some(
    (hearing) => hearing.procedure.claimId === claimId,
  );
}

/** "jeudi 12 mars 2026" — grid labels of HearingSelection.vue. */
export function formatHearingDay(date: string): string {
  return format(parse(date, "yyyy-MM-dd", new Date()), "eeee d MMMM yyyy", { locale: fr });
}

/** "12 mars 2026" — standard fr date used across Court.vue. */
export function formatHearingDate(date: Date | string): string {
  return format(new Date(date), "dd MMMM yyyy", { locale: fr });
}

export interface NextHearingLike {
  hearingDate: Date | string;
  procedureHearingType?: {
    name: ProcedureHearingType["name"];
  } | null;
}

/** Port of Court.vue nextHearingMeta. */
export function getNextHearingMeta(
  nextHearing: NextHearingLike | null | undefined,
): HearingMetadata | undefined {
  const name = nextHearing?.procedureHearingType?.name;
  return name ? HEARING_TYPES_MAPPING[name] : undefined;
}

/** Port of Court.vue nextHearingTitle — « Prochaine audience … prévue le … . » */
export function getNextHearingTitle(nextHearing: NextHearingLike | null | undefined): string {
  const meta = getNextHearingMeta(nextHearing);
  if (!nextHearing || !meta) {
    return "";
  }
  return `Prochaine ${meta.label.toLowerCase()} prévue le ${formatHearingDate(nextHearing.hearingDate)}.`;
}
